import type { Feature, FeatureCollection, Geometry } from "geojson";
import { MapData, Region, RegionProps } from "../types";

export interface NaturalEarthProps {
  ADM0_A3: string;
  NAME: string;
  NAME_EN?: string;
  NAME_PL?: string;
  TYPE?: string;
  CONTINENT?: string;
}

const WORLD_PATH = `${process.env.PUBLIC_URL}/maps/world/ne_110m_admin_0_countries.geojson`;

const pickName = (props: NaturalEarthProps): string => {
  return (
    props.NAME_PL?.trim() ||
    props.NAME_EN?.trim() ||
    props.NAME?.trim() ||
    props.ADM0_A3
  );
};

export async function loadNaturalEarthAdm0(
  filter?: (props: NaturalEarthProps) => boolean,
): Promise<MapData> {
  const response = await fetch(WORLD_PATH);
  if (!response.ok) {
    throw new Error("Failed to load Natural Earth ADM0 geojson");
  }
  const featureCollection = (await response.json()) as FeatureCollection<
    Geometry,
    NaturalEarthProps
  >;

  const regions: Region[] = featureCollection.features
    .filter((feature) => {
      const props = feature.properties as NaturalEarthProps | null | undefined;
      if (!props) {
        return false;
      }
      return filter ? filter(props) : true;
    })
    .map((rawFeature) => {
      const props = rawFeature.properties as NaturalEarthProps;
      const name = pickName(props);
      const iso = props.ADM0_A3.trim();
      const normalized: RegionProps = {
        shapeISO: iso,
        shapeName: name,
        shapeID: iso,
        shapeGroup: iso,
        shapeType: props.TYPE?.trim() || "country",
        plName: name,
      };

      const feature = {
        ...rawFeature,
        properties: normalized,
        geometry: rawFeature.geometry,
      } as Feature<Geometry, RegionProps>;

      return {
        id: normalized.shapeISO,
        props: normalized,
        feature,
        path: new Path2D(),
        bbox: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
        centroid: [0, 0],
      };
    });

  const enrichedFeatureCollection: FeatureCollection<Geometry, RegionProps> = {
    ...featureCollection,
    features: regions.map((region) => region.feature),
  };

  return { featureCollection: enrichedFeatureCollection, regions };
}
